import { useMemo } from "react";
import { z } from "zod";
import { AnnotationSchema } from "@src/features/assistantChat/ThreadMessage";
import type { Message } from "@ai-sdk/ui-utils";
import {
  useChatReferences,
  type ChatReferenceLibraryItem,
} from "./useChatReferences";

export type ValidatedAnnotation = z.infer<typeof AnnotationSchema>;

export const useMessageAnnotationsData = (
  annotations: Message["annotations"],
): {
  validatedAnnotations: ValidatedAnnotation[];
  libraryItemIds: string[];
  searchDocumentIds: string[];
  referencesLoading: boolean;
  libraryItemsByLookupId: Map<string, ChatReferenceLibraryItem>;
} => {
  const validatedAnnotations = useMemo(() => {
    const input = Array.isArray(annotations) ? annotations : [];
    const parsed = input.map((ann) => {
      const result = AnnotationSchema.safeParse(ann);
      return result;
    });
    const validated = parsed
      .filter((result): result is z.SafeParseSuccess<ValidatedAnnotation> => {
        return result.success;
      })
      .map((result) => result.data);
    return validated;
  }, [annotations]);

  // pgvector annotations carry `metadata.library_item_id`; legacy OpenAI
  // vector-store annotations don't, so we fall back to parsing the search
  // document id out of the `<uuid>.txt` filename. New chats only produce the
  // former; history rendered for an existing user can include both.
  const { libraryItemIds, searchDocumentIds } = useMemo(() => {
    const libItemIds: string[] = [];
    const searchDocIds: string[] = [];
    validatedAnnotations.forEach((ann) => {
      if (ann.type !== "file_citation") return;
      if (ann.metadata?.library_item_id) {
        libItemIds.push(ann.metadata.library_item_id);
      } else if (ann.filename?.endsWith(".txt")) {
        searchDocIds.push(ann.filename.replace(/\.txt$/, ""));
      }
    });
    return {
      libraryItemIds: Array.from(new Set(libItemIds)),
      searchDocumentIds: Array.from(new Set(searchDocIds)),
    };
  }, [validatedAnnotations]);

  const { references, loading: referencesLoading } = useChatReferences(
    libraryItemIds,
    searchDocumentIds,
  );

  const libraryItemsByLookupId = useMemo(() => {
    const map = new Map<string, ChatReferenceLibraryItem>();
    references.forEach((ref) => map.set(ref.lookupId, ref.libraryItem));
    return map;
  }, [references]);

  return {
    validatedAnnotations,
    libraryItemIds,
    searchDocumentIds,
    referencesLoading,
    libraryItemsByLookupId,
  };
};

// Minimal shape needed from Ask Opus annotations
// vectorStoreSearchResponse can be null when pgvector search results are used instead
type AskOpusAnnotationLike = {
  vectorStoreSearchResponse?:
    | {
        fileId: string;
        filename: string;
      }
    | null
    | undefined;
  pgvectorSearchResult?: {
    chunkId: number;
    libraryItemId?: string | null;
    libraryItemName?: string | null;
    sourceType?: string | null;
    intercomArticleId?: string | null;
    intercomArticleTitle?: string | null;
    intercomArticleUrl?: string | null;
    score: number;
  } | null;
};

type AskOpusFileCitationAnnotation =
  | {
      type: "file_citation";
      file_id: string | number;
      filename: string;
      index: null;
      metadata?: { library_item_id?: string | null };
    }
  | {
      type: "intercom_article";
      file_id: string | number;
      filename: string;
      index: null;
      metadata: {
        url: string;
        title?: string;
        article_id?: string | null;
        library_item_id?: string | null;
      };
    };

export const toFileCitationAnnotationsFromAskOpus = (
  askOpusAnnotations: AskOpusAnnotationLike[],
): AskOpusFileCitationAnnotation[] => {
  return (askOpusAnnotations ?? [])
    .map((ann): AskOpusFileCitationAnnotation | null => {
      // OpenAI vector store format
      if (ann.vectorStoreSearchResponse) {
        return {
          type: "file_citation",
          file_id: ann.vectorStoreSearchResponse.fileId,
          filename: ann.vectorStoreSearchResponse.filename,
          index: null,
        };
      }

      // pgvector format
      if (ann.pgvectorSearchResult) {
        const result = ann.pgvectorSearchResult;

        // Intercom article source
        if (
          result.sourceType === "INTERCOM_ARTICLE" &&
          result.intercomArticleUrl
        ) {
          return {
            type: "intercom_article",
            file_id: result.chunkId,
            filename: result.intercomArticleTitle || "Intercom Article",
            index: null,
            metadata: {
              url: result.intercomArticleUrl,
              title: result.intercomArticleTitle || undefined,
              article_id: result.intercomArticleId,
              library_item_id: result.libraryItemId,
            },
          };
        }

        // Library item source
        return {
          type: "file_citation",
          file_id: result.chunkId,
          filename: result.libraryItemName || "Unknown",
          index: null,
          metadata: { library_item_id: result.libraryItemId },
        };
      }

      return null;
    })
    .filter((ann): ann is AskOpusFileCitationAnnotation => ann != null);
};
